import { useEffect, useRef } from 'react';
import WebApp from '@twa-dev/sdk';

// The `activated` event is a newer Telegram WebApp event not yet present in
// the @twa-dev/types EventNames union. We isolate the unsafe boundary here
// so the rest of the codebase stays fully typed.
type ExtendedEventNames = Parameters<typeof WebApp.onEvent>[0] | 'activated';
const webAppOnEvent = WebApp.onEvent as (
  event: ExtendedEventNames,
  handler: () => void
) => void;
const webAppOffEvent = WebApp.offEvent as (
  event: ExtendedEventNames,
  handler: () => void
) => void;

/**
 * Calls `onRefresh` whenever the document becomes visible again or when the
 * Telegram WebApp is re-activated (e.g. user switches back from another app).
 *
 * Does NOT call `onRefresh` on mount — only on subsequent visibility changes.
 *
 * `onRefresh` is consumed via a ref so the callback does not need to be stable,
 * but wrapping it in useCallback at the call site is still recommended.
 */
export function useRefreshOnVisibility(onRefresh: () => void): void {
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onRefreshRef.current();
      }
    };

    const handleActivated = () => {
      onRefreshRef.current();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    webAppOnEvent('activated', handleActivated);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      webAppOffEvent('activated', handleActivated);
    };
  }, []);
}
